import * as React from "react"
import { connect } from "react-redux"
import store from "store"
import {routes} from "app/components/app"

import {
    css,
    Grid,
    GridCell,
    Section,
    Spinner,
    Theme,
    Icon,
} from "@bdfx/mergado-ui-kit"

import TemplateSidebar from "template-previews/components/templates-sidebar"
import TemplatePreviews from "template-previews/components/template-previews"
import Togglers from "template-previews/components/togglers"

import {setActiveRoute} from "actions"
import {setSizesToStore, setTemplatesToStore} from 'template-edit/reducers'
import {getProjectSettings as apiGetProjectSettings, getSizes, getTemplates} from "app/helpers/api"
import { getFormatId, getTemplateId, } from "app/helpers/url-params"
import {PreviewsForm, DEFAULT_PRODUCTS_PER_PAGE, PreviewsData} from "template-previews/types";
import { initForm, updateInputValue as change } from "app/reducers/form"
import { defaultPreviewsSettings, PreviewsSettings } from "./types";
import { getPreviews as getPreviewsApi } from "app/helpers/api";
import { SettingsFormType, Template } from "app/types/model-types";
import trans from "trans"
import Buttons from "./components/buttons";
import {inIframe} from "app/helpers/mergado";
import { AxiosResponse } from "axios"
import { ObjectsType } from "products/components/watermark-marker-common"
import { selectFormValues } from "app/reducers/form"
import { InvertMaskModeType } from "products/components/invert-mask-marker"
import { SizeItem } from "settings/components/sizes-settings"

export interface Props {
    previewsForm: PreviewsForm
    settingsForm: SettingsFormType
    change: typeof change
    initForm: typeof initForm
    setSizesToStore: typeof setSizesToStore
    setActiveRoute: typeof setActiveRoute
}

export interface State {
    isLoading: boolean
    isLoaded: boolean
    randomNumber: number
    previews: PreviewsData
}

class Previews extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        const randomNumber = new Date().getTime();

        this.state = {
            isLoading: false,
            isLoaded: false,
            randomNumber: randomNumber,
            previews: null,
        }

        if(Object.keys(props.previewsForm).length === 0) {
            props.initForm("previewsForm", {
                view: defaultPreviewsSettings,
                randomNumber: randomNumber,
            })
        }
    }

    componentDidMount() {
        const formatId = getFormatId()
        const templateId = getTemplateId()

        this.setState({ isLoading: true, isLoaded: false })

        this.props.setActiveRoute(routes.SHOWROOM)

        getSizes()
            .then((response: AxiosResponse<SizeItem[]>) => {
                this.props.setSizesToStore(response.data)
            })
            .catch((e) => console.error('cannot get sizes', e))


        getTemplates()
            .then((response: AxiosResponse<Template[]>) => {
                store.dispatch(setTemplatesToStore(response.data))
            })
            .catch(e => {
                console.error('error white getting templates', e.response ?? null)
            })
            .finally(() => {
                this.setState({ isLoading: false })
            })

        apiGetProjectSettings()
            .then((response: AxiosResponse<SettingsFormType>) => {
                if (Object.keys(this.props.settingsForm).length === 0) {
                    this.props.initForm("settingsForm", response.data)
                }

                const view: PreviewsSettings = {
                    type: templateId ? 'template' : 'size',
                    id: formatId ? formatId : 'ALL',
                    page: 1,
                    rawImages: 0,
                    allowCache: 1,
                    active: 1,
                    itemsPerPage: response.data.products_per_page,
                }

                this.props.change('previewsForm', 'view', view)

                this.getPreviews({
                    view: view,
                    randomNumber: this.state.randomNumber
                })

                this.setState({ isLoaded: true })
            })
            .catch(error => {
                console.error('cannot get settings', error, error.response)
                this.setState({ isLoaded: true })
            })
            .finally(() => this.setState({ isLoading: false }))
    }

    getPreviews = (previewsForm : PreviewsForm) : void => {
        this.setState({isLoading: true})

        getPreviewsApi(previewsForm)
            .then((response: AxiosResponse<PreviewsData>) => {
                this.setState({ previews: response.data, isLoaded: true })
            })
            .catch(error => {
                console.debug('error while loading previews', error)
                this.setState({isLoaded: false})
            })
            .finally(() => this.setState({isLoading: false}))
    }

    updatePreviews = (previewsForm: PreviewsForm) : void => {
        this.getPreviews(previewsForm)
    }

    updateUserSetObjects = (idx: number, objects: ObjectsType, mode: InvertMaskModeType = null) => {
        const { previews } = this.state
        let updateditems = [...previews.items]
        updateditems[idx].userSetObjects = JSON.stringify(objects)
        updateditems[idx].maskMode = mode || null
        this.setState({
            previews: {
                ...previews,
                items: updateditems,
            }
        })
        this.updatePreviews(this.props.previewsForm)
    }

    render() {
        const { isLoading, previews } = this.state
        const {previewsForm} = this.props

        return (
            <StyledSection
                prefix={<Icon type={'th'} text={trans('previews')} size={20} color={Theme.blue} />}
                className={`previews-section ${isLoading ? 'muk-helper-loading-line with-line' : ''}`}
                header={<Togglers updatePreviews={this.updatePreviews}/>}
                suffix={<Buttons updatePreviews={this.updatePreviews} />}>
                {previewsForm && previewsForm.view ?
                <StyledGrid gap={"5px"}>
                    <GridCell>
                        <TemplateSidebar updatePreviews={this.updatePreviews}/>
                    </GridCell>

                    <GridCell>
                        <TemplatePreviews
                            previews={previews}
                            isLoading={isLoading}
                            updatePreviews={this.updatePreviews}
                            updateUserSetObjects={this.updateUserSetObjects}
                        />
                    </GridCell>
                </StyledGrid>
                    :
                    <Spinner type={"bubbles"}/>
                }
            </StyledSection>
        )
    }
}

const StyledGrid = css(Grid)({
    paddingLeft: 0,
    gridTemplateColumns: '350px auto',
    alignSelf: 'center',
    '@media screen and (max-width: 1024px)': {
        gridTemplateColumns: 'auto',
    },
    ' .muk-inputgroupv__queries': {
        marginLeft: '-20px',
    }
})

const StyledSection = css(Section)({
    margin: '0',
    ' .m-prefix': {
        paddingTop: '25px'
    },
    ' .m-suffix': {
        height: 'auto',
        ' .muk-button': {
            padding: '5px 14px 5px',
        }
    },
    ' .m-header-content': {
        padding: '0 !important',
        margin: 0,
        alignSelf: 'center',
    },
    ' .m-content': {
        padding: 0,
    },
    '@media screen and (max-width: 1024px)': {
        ' .m-prefix': {
            display: 'none',
            visibility: 'hidden',
        },
        ' .m-suffix': {
            position: 'absolute',
            right: 0,
            padding: 0,
            height: 'auto',
            top: '10px',
        },
        ' .m-suffix .muk-icon__text': {
            display: 'none',
            visibility: 'hidden',
        },
        ' .m-header-content': {
            paddingLeft: '20px !important',
        },
        ' .m-content': {
            paddingBottom: inIframe() ? '100px' : '10px',
            overflow: 'auto',
        },
    },
})

function mapStateToProps(state) {
    return {
        previewsForm: selectFormValues(state, 'previewsForm'),
        settingsForm: selectFormValues(state, 'settingsForm'),
    }
}

export default connect(mapStateToProps, {
    change,
    initForm,
    setSizesToStore,
    setActiveRoute,
})(Previews)
